// Import-specific constants for AWS Connect resource import

// Resource types that can be imported (in dependency order)
export const IMPORTABLE_RESOURCE_TYPES = [
  'hoursOfOperation',
  'userHierarchies',
  'agentStatus',
  'queues',
  'routingProfiles',
  'quickConnects'
];

// Import order (critical for dependency resolution)
export const IMPORT_ORDER = [
  'hoursOfOperation',    // No dependencies
  'userHierarchies',     // No dependencies (but parent→child order required)
  'agentStatus',         // No dependencies
  'queues',              // Depends on: hoursOfOperation, contactFlows (reference only)
  'routingProfiles',     // Depends on: queues
  'quickConnects'        // Depends on: queues, users (reference only), contactFlows (reference only)
];

// Human-readable resource type labels
export const RESOURCE_TYPE_LABELS = {
  hoursOfOperation: 'Hours of Operation',
  userHierarchies: 'User Hierarchies',
  agentStatus: 'Agent Status',
  queues: 'Queues',
  routingProfiles: 'Routing Profiles',
  quickConnects: 'Quick Connects'
};

// Sheet names mapping (use existing constants from excel/constants.js)
export const RESOURCE_TYPE_TO_SHEET = {
  hoursOfOperation: 'HOURS_OF_OPERATION',
  userHierarchies: 'USER_HIERARCHY_DATA',
  agentStatus: 'AGENT_STATUS',
  queues: 'QUEUES',
  routingProfiles: 'ROUTING_PROFILES',
  quickConnects: 'QUICK_CONNECTS'
};

// Import status values
export const IMPORT_STATUS = {
  IDLE: 'idle',
  VALIDATING: 'validating',
  READY: 'ready',
  IMPORTING: 'importing',
  COMPLETE: 'complete',
  ERROR: 'error'
};

// Conflict resolution strategies
export const CONFLICT_RESOLUTION = {
  SKIP: 'skip',
  UPDATE: 'update'
};

// Import action types (for logging)
export const IMPORT_ACTION = {
  CREATE: 'create',
  UPDATE: 'update',
  SKIP: 'skip',
  ERROR: 'error'
};

// Resource state during import
export const RESOURCE_STATE = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed',
  SKIPPED: 'skipped'
};

// Quick Connect types
export const QUICK_CONNECT_TYPE = {
  PHONE_NUMBER: 'PHONE_NUMBER',
  QUEUE: 'QUEUE',
  USER: 'USER'
};

// Rate limiting configuration for import operations
// These match the export operation limits but are optimized for create/update operations
export const IMPORT_RATE_LIMITS = {
  hoursOfOperation: {
    maxConcurrent: 2,
    delayBetweenRequests: 500
  },
  userHierarchies: {
    maxConcurrent: 2,
    delayBetweenRequests: 500
  },
  agentStatus: {
    maxConcurrent: 2,
    delayBetweenRequests: 500
  },
  queues: {
    maxConcurrent: 2,
    delayBetweenRequests: 500
  },
  routingProfiles: {
    maxConcurrent: 2,
    delayBetweenRequests: 500
  },
  quickConnects: {
    maxConcurrent: 2,
    delayBetweenRequests: 500
  }
};

// Dependency mappings
export const RESOURCE_DEPENDENCIES = {
  hoursOfOperation: [],
  userHierarchies: [], // Parent groups must be created before children
  agentStatus: [],
  queues: ['hoursOfOperation'], // Also references contactFlows but not created during import
  routingProfiles: ['queues'],
  quickConnects: ['queues'] // Also references users and contactFlows but not created
};

// Resources that are referenced but not imported
export const REFERENCE_ONLY_RESOURCES = [
  'contactFlows',
  'users',
  'securityProfiles'
];

// Validation warning types
export const WARNING_TYPE = {
  MISSING_DEPENDENCY: 'missing_dependency',
  CONFLICT: 'conflict',
  INVALID_DATA: 'invalid_data',
  REFERENCE_NOT_FOUND: 'reference_not_found'
};

// User hierarchy levels (1-5)
export const USER_HIERARCHY_LEVELS = [1, 2, 3, 4, 5];

// Maximum items per batch operation
export const MAX_BATCH_SIZE = 50;

// Error messages
export const ERROR_MESSAGES = {
  FILE_PARSE_ERROR: 'Failed to parse Excel file. Please ensure the file is in the correct format.',
  VALIDATION_ERROR: 'Validation failed. Please review the warnings before proceeding.',
  IMPORT_ERROR: 'Import operation failed. See log for details.',
  INSTANCE_NOT_SELECTED: 'Please select a target Connect instance.',
  FILE_NOT_UPLOADED: 'Please upload an Excel file to import.',
  NO_RESOURCES_SELECTED: 'Please select at least one resource type to import.',
  AWS_API_ERROR: 'AWS API error occurred during import. Check your credentials and permissions.',
  NETWORK_ERROR: 'Network error occurred. Please check your connection and try again.'
};
